// src/services/branchService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class BranchService {
  // Get all branches
  async getAllBranches(params = {}) {
    try {
      console.log('🏢 Fetching all branches with params:', params)
      const response = await ApiService.get(AppURLs.BRANCHES.GET_ALL, params)
      console.log('✅ Branches fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branches:', error)
      throw error
    }
  }

  // Get branch by ID
  async getBranchById(id) {
    try {
      console.log('🏢 Fetching branch by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Branch fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch:', error)
      throw error
    }
  }

  // Create new branch
  async createBranch(branchData) {
    try {
      console.log('🏢 Creating new branch with data:', branchData)
      const response = await ApiService.post(AppURLs.BRANCHES.CREATE, branchData)
      console.log('✅ Branch created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating branch:', error)
      throw error
    }
  }

  // Update branch using PATCH
  async updateBranch(id, branchData) {
    try {
      console.log('🏢 Updating branch:', { id, branchData })
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      console.log('📡 Full URL:', `${AppURLs.API}${endpoint}`)
      
      // Use PATCH instead of PUT for updates
      const response = await ApiService.patch(endpoint, branchData)
      console.log('✅ Branch updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating branch:', error)
      throw error
    }
  }

  // Partially update branch (same as updateBranch since we're using PATCH)
  async patchBranch(id, branchData) {
    try {
      console.log('🏢 Patching branch:', { id, branchData })
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.UPDATE, { id })
      const response = await ApiService.patch(endpoint, branchData)
      console.log('✅ Branch patched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error patching branch:', error)
      throw error
    }
  }

  // Delete branch
  async deleteBranch(id) {
    try {
      console.log('🏢 Deleting branch:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Branch deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting branch:', error)
      throw error
    }
  }

  // Toggle branch status using PATCH
  async toggleBranchStatus(id, isActive) {
    try {
      console.log('🏢 Toggling branch status:', { id, isActive })
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.UPDATE, { id })
      const response = await ApiService.patch(endpoint, { isActive })
      console.log('✅ Branch status toggled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling branch status:', error)
      throw error
    }
  }

  // Search branches
  async searchBranches(searchTerm, filters = {}) {
    try {
      console.log('🏢 Searching branches:', { searchTerm, filters })
      const params = {
        search: searchTerm,
        ...filters
      }
      const response = await ApiService.get(AppURLs.BRANCHES.GET_ALL, params)
      console.log('✅ Branches search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching branches:', error)
      throw error
    }
  }

  // Get branches with pagination
  async getBranchesPaginated(page = 1, limit = 10, filters = {}) {
    try {
      console.log('🏢 Fetching paginated branches:', { page, limit, filters })
      const params = {
        page,
        limit,
        ...filters
      }
      const response = await ApiService.get(AppURLs.BRANCHES.GET_ALL, params)
      console.log('✅ Paginated branches fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching paginated branches:', error)
      throw error
    }
  }

  // Bulk operations
  async bulkDeleteBranches(branchIds) {
    try {
      console.log('🏢 Bulk deleting branches:', branchIds)
      const response = await ApiService.post(`${AppURLs.BRANCHES.GET_ALL}/bulk-delete`, {
        ids: branchIds
      })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting branches:', error)
      throw error
    }
  }

  async bulkUpdateBranchStatus(branchIds, isActive) {
    try {
      console.log('🏢 Bulk updating branch status:', { branchIds, isActive })
      const response = await ApiService.post(`${AppURLs.BRANCHES.GET_ALL}/bulk-status`, {
        ids: branchIds,
        isActive
      })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating branch status:', error)
      throw error
    }
  }

  // Export branches
  async exportBranches(format = 'csv', filters = {}) {
    try {
      console.log('🏢 Exporting branches:', { format, filters })
      const params = {
        format,
        ...filters
      }
      const response = await ApiService.get(`${AppURLs.BRANCHES.GET_ALL}/export`, params)
      console.log('✅ Branches exported:', response)
      return response
    } catch (error) {
      console.error('❌ Error exporting branches:', error)
      throw error
    }
  }

  // Get branch statistics
  async getBranchStats() {
    try {
      console.log('🏢 Fetching branch statistics')
      const response = await ApiService.get(`${AppURLs.BRANCHES.GET_ALL}/stats`)
      console.log('✅ Branch stats fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch stats:', error)
      throw error
    }
  }
}

export default new BranchService()